import { Ellipsis, RotateCcw } from "lucide-react";
import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { PayrollEditorQueries } from "@/payroll/editor/query";
import { proposalError } from "@/payroll/editor/state";
import type { Workspace } from "@/payroll/editor/types";

/**
 * The occasional proposal actions, beside Publish.
 *
 * Publishing is the thing an owner comes here to do, so it keeps its own
 * button. Starting over is rare and cannot be undone, so it sits behind the
 * tertiary menu. More controls can join it here rather than crowding the row.
 *
 * Before this, an owner who did not want their proposal had no way to drop it.
 * A real owner asked for exactly that, and the only honest answer was to tell
 * them to contact us.
 */
export const ProposalMenu = ({
  workspace,
  discard,
  busy,
}: {
  workspace: Workspace;
  discard: PayrollEditorQueries["discard"];
  busy: boolean;
}) => {
  const [confirming, setConfirming] = useState(false);
  if (!workspace.editable) return null;

  // A disabled item says why in the same row, rather than going quiet and
  // leaving the reader to guess which of the two reasons applies.
  const reason = busy
    ? "Wait for the current change to finish"
    : !workspace.has_changes
      ? "Nothing to start over from"
      : "";

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="tertiary"
            // Level with Publish beside it. `icon` is 40px and the pill is 32,
            // and the two sat in the same row at different heights. There is
            // no 32px square in the size scale to ask for instead.
            className="h-8 w-8 min-w-8"
            aria-label="More proposal actions"
            leftIcon={<Ellipsis className="h-4 w-4" />}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-48">
          <DropdownMenuItem
            disabled={!!reason || discard.isPending}
            onSelect={() => {
              discard.reset();
              setConfirming(true);
            }}
          >
            <RotateCcw className="h-4 w-4" />
            <Stack gap="none">
              <Typography variant="bodyW3">Start over</Typography>
              {!!reason && (
                <Typography variant="bodyW3" textColor="tertiary">
                  {reason}
                </Typography>
              )}
            </Stack>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={confirming} onOpenChange={setConfirming}>
        <DialogContent className="max-w-md">
          <DialogHeader className="p-6 pb-0 text-left">
            <DialogTitle>Start over?</DialogTitle>
            {/* The primitive is `sr-only` by default, so a description meant to be
                read needs this. The publish dialog had the same gap. */}
            <DialogDescription className="not-sr-only pt-2">
              This drops the proposed rules and this conversation, and puts back
              anyone this proposal moved on the roster. Roster changes somebody
              made by hand stay as they are. Your published rules do not change,
              and nobody's pay moves.
            </DialogDescription>
          </DialogHeader>
          {!!discard.error && (
            <p role="alert" className="px-6 text-sm text-negative-text">
              {proposalError(discard.error)}
            </p>
          )}
          <DialogFooter className="gap-2 p-6">
            <Button
              size="sm"
              variant="tertiary"
              onClick={() => setConfirming(false)}
            >
              Keep it
            </Button>
            <Button
              size="sm"
              loading={discard.isPending}
              onClick={() =>
                discard.mutate(undefined, {
                  onSuccess: () => setConfirming(false),
                })
              }
            >
              Start over
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
